import React, { Component } from "react";
import { Form, Button, Alert, Modal, Dropdown } from "react-bootstrap";
import { VscSend } from "react-icons/vsc";
import { LuClipboardCopy, LuCheck, LuPlus, LuX, LuChevronDown, LuChevronUp } from "react-icons/lu";
import { CgDetailsMore } from "react-icons/cg";
import AceEditor from "react-ace";
import Tooltip from "../../../controls/common/tooltip";
import Clipboard from "../../../controls/common/clipboard";
import WidgetBase from "../common/widgetBase";
import EnvelopeControls from "../common/envelopeControls";
import { MarkdownBody } from "../common/markdown";
import { Converter } from "../../workspace/converter";
import Navigation from "context/nav";
import Utils from "context/utils";
import { withPreferences } from "context/preferences";
import "./index.css";

// Custom Dropdown toggle for the composer's "+" button. Lets us keep the
// existing circular styling while delegating click/keyboard handling to
// react-bootstrap's Dropdown.
const ComposerAttachToggle = React.forwardRef(({ onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    className="playground-thread-composer-attach"
    onClick={(e) => { e.preventDefault(); onClick(e); }}
    aria-label="Attachment menu"
  >
    <LuPlus />
  </button>
));

// Small icon-button used in the bottom-of-bubble action bar. Briefly
// swaps to a "confirmed" icon after activation so the user sees the
// action register (Clipboard.to has no visible side-effect on its own).
class ThreadItemAction extends Component {
  constructor(props) {
    super(props);
    this.state = { confirmed: false };
    this.timer = null;
  }

  componentWillUnmount() {
    if ( this.timer ) clearTimeout(this.timer);
  }

  handleClick = () => {
    if ( this.props.onActivate ) this.props.onActivate();
    if ( this.props.confirmIcon ) {
      this.setState({ confirmed: true });
      if ( this.timer ) clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.setState({ confirmed: false });
        this.timer = null;
      }, 1200);
    }
  }

  render() {
    const showConfirm = this.state.confirmed && this.props.confirmIcon;
    return (
      <Tooltip text={showConfirm ? (this.props.confirmTooltip || this.props.tooltip) : this.props.tooltip}>
        <button
          type="button"
          className="playground-thread-action"
          onClick={this.handleClick}
        >
          {showConfirm ? this.props.confirmIcon : this.props.icon}
        </button>
      </Tooltip>
    );
  }
}

const PAGE_LIMIT = 10;

class ThreadNode extends WidgetBase {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      oldestCreatedAt: null,
      newestCreatedAt: null,
      hasMoreOlder: false,
      loading: false,
      loadingOlder: false,
      posting: false,
      inputValue: "",
      currentUserId: null,
      unseenCount: 0,
      loadError: null,
      postError: null,
      // Composer metadata attached to the next send.
      composerMetadata: null,            // parsed object or null
      composerMetadataModalVisible: false,
      composerMetadataDraft: "",         // textarea contents in the modal
      composerMetadataError: null,
      // Item ids with content expanded: their content items lose the
      // collapsed max-height cap. Surfaced (via the content toggle) only for
      // items that overflow the cap (detected after layout in componentDidUpdate).
      expandedContent: new Set(),
      // Item ids with the metadata block shown. Independent of content
      // expansion; the metadata toggle appears whenever the item has metadata.
      expandedMetadata: new Set(),
      overflowingItems: new Set()
    };

    this.scrollContainerRef = React.createRef();
    this.nodeRef = React.createRef();
    this.textareaRef = React.createRef();
    this.wasAtBottom = true;
    this.pendingTempIds = new Set();
    this.authorPromises = {};
    this.boundAlias = null;
    this.boundCallback = null;
    this.boundSessionId = null;
    this.mounted = false;
    this.resizeObserver = null;
    // Per-item callback refs for the rendered content blocks; used to
    // measure scrollHeight vs offsetHeight after each render and decide
    // whether the item should surface the expand toggle.
    this.contentRefs = {};
  }

  /**
   * Lifecycle
   */

  componentDidMount() {
    super.componentDidMount();
    this.mounted = true;

    const api = this.getApi();
    if ( api && api.getCurrentUserId ) {
      api.getCurrentUserId().then( (id) => {
        if ( !this.mounted ) return;
        this.setState({ currentUserId: id });
      }, (_error) => {
        // Anonymous — leave currentUserId as null
      });
    }

    // Observe widget width to toggle the is-narrow class for the
    // bubble-full-width behavior. Doing this in JS (not via CSS
    // container queries) avoids inline-size containment interactions
    // with the grid-mode flex height chain that were breaking scroll.
    if ( typeof ResizeObserver !== "undefined" && this.nodeRef.current ) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for ( const entry of entries ) {
          const w = entry.contentRect.width;
          const isNarrow = w > 0 && w <= 700;
          const node = entry.target;
          if ( isNarrow ) node.classList.add("is-narrow");
          else node.classList.remove("is-narrow");
        }
      });
      this.resizeObserver.observe(this.nodeRef.current);
    }

    this.checkReload();
  }

  componentDidUpdate(prevProps) {
    super.componentDidUpdate(prevProps);
    this.checkReload();
    this.checkSyncResume();
    this.maybeAutoLoadOlder();
    this.checkContentOverflow();
  }

  // If the loaded items don't fill the viewport AND there are more older
  // items on the server, fetch the next older page automatically. Without
  // this, backward pagination is gated on the user scrolling to the top
  // — which they can't do when there's no scrollbar in the first place.
  maybeAutoLoadOlder() {
    if ( this.state.loading ) return;
    if ( this.state.loadingOlder ) return;
    if ( !this.state.hasMoreOlder ) return;
    if ( this.state.items.length === 0 ) return;
    const el = this.scrollContainerRef.current;
    if ( !el ) return;
    // 1px slop to absorb sub-pixel rounding from CSS sizing.
    if ( el.scrollHeight > el.clientHeight + 1 ) return;
    this.loadOlder({ auto: true });
  }

  // When sync flips from OFF -> ON, actively re-tail the thread from the
  // widget side. The session-events catch-up dispatches
  // session_thread_item_posted events, but: (a) the widget's
  // newestCreatedAt is the user's own latest post — items posted by
  // other actors BEFORE the user's post would be missed by a
  // `createdSince: newestCreatedAt` fetch, and (b) we don't want to
  // depend on the event channel being intact through every sync edge
  // case (race between toggle and outstanding POSTs, polling pauses
  // after 180s, etc).
  //
  // Strategy:
  //   - if we have a local baseline (newestCreatedAt is set), fetch
  //     strictly-newer items past it and dedup against state.items
  //   - if we don't (initial state, or items wiped), run initialLoad
  //     which does a full first-page fetch
  checkSyncResume() {
    const currentSync = !!(this.context && this.context.sessionSync);
    const prevSync = this._lastSyncState;
    this._lastSyncState = currentSync;

    if ( prevSync !== false || currentSync !== true ) return;
    if ( this.getEnvelopeState().status !== "ready" ) return;

    if ( this.state.newestCreatedAt ) {
      this.refetchTail();
    } else {
      this.initialLoad();
    }
  }

  refetchTail() {
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();
    if ( !alias || !sessionId || !revisionId ) return;

    const api = this.getApi();
    api.threadItemsList(revisionId, sessionId, alias, {
      ascending: true,
      createdSince: this.state.newestCreatedAt
    }).then( (response) => {
      if ( !this.mounted ) return;
      if ( alias !== this.getBoundAlias() || sessionId !== this.getSessionId() ) return;

      const rawItems = (response.data && response.data["items"]) || [];
      const existingIds = new Set(this.state.items.map(i => i.id));
      const newItems = rawItems.filter(i => !existingIds.has(i.id));
      if ( newItems.length === 0 ) return;

      const lastCreated = newItems[newItems.length - 1].created_at;
      this.setState({
        items: [...this.state.items, ...newItems],
        newestCreatedAt: lastCreated,
        unseenCount: this.wasAtBottom ? 0 : this.state.unseenCount + newItems.length
      }, () => {
        if ( this.wasAtBottom ) {
          this.scrollToBottom();
        }
      });
    }, (_error) => {
      // Quiet fail — next event or next sync resume will retry.
    });
  }

  // Reload-on-readiness gate: we reload when alias/session changes OR when the
  // envelope transitions to ready (e.g. session-objects list arrives, the
  // specific object's value finishes loading). Keyed comparison avoids
  // redundant fetches and ensures we don't fire backend calls against an
  // envelope that's still loading, missing, or of the wrong type.
  checkReload() {
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const ready = this.getEnvelopeState().status === "ready";
    const key = `${alias || ""}|${sessionId || ""}|${ready ? "ready" : "wait"}`;
    if ( key === this._lastLoadKey ) return;
    this._lastLoadKey = key;
    this.boundSessionId = sessionId;
    this.initialLoad();
  }

  componentWillUnmount() {
    this.mounted = false;
    this.unsubscribeFromThreadEvents();
    if ( this.resizeObserver ) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    super.componentWillUnmount();
  }

  /**
   * Accessors
   */

  getApi() {
    return this.props.data.shellInterface.getApp().api;
  }

  getRevisionId() {
    return this.props.data.shellInterface.getRevision()["id"];
  }

  getSessionId() {
    return this.context && this.context.session ? this.context.session.id : null;
  }

  getBoundAlias() {
    return Converter.getComponentPortValueData(this.props.data.component, "thread");
  }

  // Inspect session-manager state to decide what we can render and whether
  // it's safe to call the backend. Returns:
  //   - { status: "unbound" }        — no thread port binding
  //   - { status: "no-session" }     — alias set, no session selected
  //   - { status: "loading" }        — session-objects list or value still loading
  //   - { status: "missing" }        — list loaded, no object with this alias
  //   - { status: "wrong-type" }     — object exists but value.type !== "thread"
  //   - { status: "ready", value }   — object exists and is a thread
  // This also registers a subscription on the alias via `discoveredAliases`
  // so the widget re-renders when the value becomes available.
  getEnvelopeState() {
    const alias = this.getBoundAlias();
    if ( !alias ) return { status: "unbound" };

    const manager = this.context;
    if ( !manager || !manager.session ) return { status: "no-session" };

    // Subscribe to the alias so this widget re-renders when its value
    // arrives. Cleared per-render by WidgetBase.updateSubscriptions().
    this.discoveredAliases.add(alias);

    const sessionObjects = manager.sessionObjects;
    if ( sessionObjects === null || sessionObjects === undefined ) {
      return { status: "loading", reason: "Loading session objects..." };
    }

    const exists = sessionObjects.some(o => o.alias === alias);
    if ( !exists ) {
      return { status: "missing", alias };
    }

    const value = manager.getSessionData ? manager.getSessionData(alias) : undefined;
    if ( value === undefined || value === null ) {
      return { status: "loading", reason: "Loading thread..." };
    }

    if ( value.type !== "thread" ) {
      return { status: "wrong-type", alias, type: value.type || "unknown" };
    }

    return { status: "ready", value };
  }

  /**
   * Data loading
   */

  initialLoad() {
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();

    // Reset listener binding before re-evaluating
    this.unsubscribeFromThreadEvents();

    // Record the alias/session we're loading against so componentDidUpdate
    // doesn't fire a redundant reload while this call is in flight.
    this.boundAlias = alias;
    this.boundSessionId = sessionId;

    // Don't fetch unless the envelope is a confirmed thread. For unbound /
    // no-session / loading / missing / wrong-type states the UI shows the
    // appropriate banner; firing list_session_thread_items here would just
    // produce an invalid_thread_envelope error and a confusing second
    // warning in the body.
    const envelopeState = this.getEnvelopeState();
    if ( !alias || !sessionId || !revisionId || envelopeState.status !== "ready" ) {
      this.setState({
        items: [],
        oldestCreatedAt: null,
        newestCreatedAt: null,
        hasMoreOlder: false,
        loading: false,
        loadError: null,
        unseenCount: 0
      });
      return;
    }

    this.setState({
      loading: true,
      items: [],
      oldestCreatedAt: null,
      newestCreatedAt: null,
      hasMoreOlder: false,
      loadError: null,
      unseenCount: 0
    });
    this.wasAtBottom = true;

    const api = this.getApi();
    api.threadItemsList(revisionId, sessionId, alias, { ascending: false }).then( (response) => {
      if ( !this.mounted ) return;
      // Defend against late responses for a stale alias/session.
      if ( alias !== this.getBoundAlias() || sessionId !== this.getSessionId() ) return;

      const rawItems = (response.data && response.data["items"]) || [];
      // Backend returns newest-first when ascending=false; display oldest-first.
      const items = rawItems.slice().reverse();
      this.setState({
        items: items,
        oldestCreatedAt: items.length > 0 ? items[0].created_at : null,
        newestCreatedAt: items.length > 0 ? items[items.length - 1].created_at : null,
        hasMoreOlder: rawItems.length === PAGE_LIMIT,
        loading: false,
        loadError: null
      }, () => {
        this.scrollToBottom();
      });
      this.subscribeToThreadEvents();
    }, (error) => {
      if ( !this.mounted ) return;
      this.setState({
        loading: false,
        loadError: error && error.message ? error.message : "Failed to load thread items."
      });
    });
  }

  loadOlder(opts) {
    if ( !this.state.hasMoreOlder || this.state.loadingOlder || !this.state.oldestCreatedAt ) {
      return;
    }
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();
    if ( !alias || !sessionId || !revisionId ) return;

    // `auto` means the fetch was triggered because the messages list
    // didn't fill its viewport — keep the newest messages visible at the
    // bottom after the prepend. `manual` (user scrolled to top) preserves
    // the visual anchor so the user stays on the item they were viewing.
    const auto = !!(opts && opts.auto);

    this.setState({ loadingOlder: true });

    const scrollContainer = this.scrollContainerRef.current;
    const capturedScrollHeight = scrollContainer ? scrollContainer.scrollHeight : 0;

    const api = this.getApi();
    api.threadItemsList(revisionId, sessionId, alias, {
      ascending: false,
      createdBefore: this.state.oldestCreatedAt
    }).then( (response) => {
      if ( !this.mounted ) return;
      if ( alias !== this.getBoundAlias() || sessionId !== this.getSessionId() ) {
        this.setState({ loadingOlder: false });
        return;
      }

      const rawItems = (response.data && response.data["items"]) || [];
      // Backend returns newest-first; reverse for display order (oldest-first).
      const olderItems = rawItems.slice().reverse();

      if ( olderItems.length === 0 ) {
        this.setState({ loadingOlder: false, hasMoreOlder: false });
        return;
      }

      this.setState({
        items: [...olderItems, ...this.state.items],
        oldestCreatedAt: olderItems[0].created_at,
        hasMoreOlder: rawItems.length === PAGE_LIMIT,
        loadingOlder: false
      }, () => {
        const el = this.scrollContainerRef.current;
        if ( !el ) return;
        if ( auto ) {
          // Auto-fill: there was nothing to scroll before the prepend.
          // Keep the latest messages at the bottom of the viewport, the
          // older ones fill in above. componentDidUpdate's
          // maybeAutoLoadOlder will trigger again on the next render if
          // the viewport still isn't full and there's more to fetch.
          el.scrollTop = el.scrollHeight;
        } else {
          // Manual: preserve the visual anchor — user stays where they
          // were even though new content was prepended above.
          el.scrollTop = el.scrollHeight - capturedScrollHeight;
        }
      });
    }, (_error) => {
      if ( !this.mounted ) return;
      // Backward pagination is best-effort; fail silently.
      this.setState({ loadingOlder: false });
    });
  }

  /**
   * Live-tail subscription
   */

  subscribeToThreadEvents() {
    this.unsubscribeFromThreadEvents();
    const alias = this.getBoundAlias();
    if ( !alias || !this.context ) return;
    this.boundCallback = (payload) => this.onThreadItemPosted(payload);
    this.boundAlias = alias;
    this.context.addThreadEventListener(alias, this.boundCallback);
  }

  unsubscribeFromThreadEvents() {
    if ( this.boundAlias && this.boundCallback && this.context ) {
      this.context.removeThreadEventListener(this.boundAlias, this.boundCallback);
    }
    this.boundAlias = null;
    this.boundCallback = null;
  }

  onThreadItemPosted(payload) {
    if ( !payload ) return;
    const eventAlias = payload.alias;
    const itemId = payload.item_id;
    if ( eventAlias !== this.boundAlias ) return;
    // De-dup against our own optimistic post (which will land via the POST
    // response handler) or against an already-known id.
    if ( itemId && this.state.items.some(i => i.id === itemId) ) return;

    const alias = this.boundAlias;
    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();
    if ( !alias || !sessionId || !revisionId ) return;

    const api = this.getApi();
    api.threadItemsList(revisionId, sessionId, alias, {
      ascending: true,
      createdSince: this.state.newestCreatedAt
    }).then( (response) => {
      if ( !this.mounted ) return;
      if ( alias !== this.boundAlias || sessionId !== this.getSessionId() ) return;

      const rawItems = (response.data && response.data["items"]) || [];
      const existingIds = new Set(this.state.items.map(i => i.id));
      const newItems = rawItems.filter(i => !existingIds.has(i.id));
      if ( newItems.length === 0 ) return;

      const lastCreated = newItems[newItems.length - 1].created_at;
      this.setState({
        items: [...this.state.items, ...newItems],
        newestCreatedAt: lastCreated,
        unseenCount: this.wasAtBottom ? 0 : this.state.unseenCount + newItems.length
      }, () => {
        if ( this.wasAtBottom ) {
          this.scrollToBottom();
        }
      });
    }, (_error) => {
      // Ignore — the next event will retry the catch-up fetch.
    });
  }

  /**
   * Posting
   */

  canSend() {
    return !!(
      this.state.inputValue.trim()
      && !this.state.posting
      && this.state.currentUserId
      && this.getEnvelopeState().status === "ready"
      && this.props.data.shellInterface.canEditRevision()
    );
  }

  sendItem(textOverride) {
    const text = (textOverride !== undefined ? textOverride : this.state.inputValue).trim();
    if ( !text || this.state.posting ) return;

    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();
    if ( !alias || !sessionId || !revisionId ) return;

    const metadata = this.state.composerMetadata; // null if no attachment

    // Split the composer text into multiple content items at "\n---\n"
    // separators. Each non-empty segment becomes its own text content
    // item, so the user can compose a single send that contains a few
    // logically distinct chunks. Empty segments (e.g. leading/trailing
    // separators) are dropped.
    const content = text
      .split(/\n---\n/)
      .map(segment => segment.trim())
      .filter(segment => segment.length > 0)
      .map(segment => ({ type: "text", text: segment }));
    if ( content.length === 0 ) return;

    const tempId = Utils.generateUniqueId();
    this.pendingTempIds.add(tempId);

    const optimisticItem = {
      id: tempId,
      __tempId: tempId,
      __pending: true,
      user_id: this.state.currentUserId,
      content,
      created_at: Utils.currentTimeUtcIso()
    };
    if ( metadata ) {
      optimisticItem.metadata = metadata;
    }

    this.wasAtBottom = true;
    this.setState({
      items: [...this.state.items, optimisticItem],
      inputValue: "",
      posting: true,
      postError: null,
      // Clear the attached metadata once it has been queued — a fresh
      // attachment is required for the next send.
      composerMetadata: null
    }, () => {
      // Reset textarea height after clearing input, and keep focus in
      // the composer so the user can start typing the next message
      // immediately. (The button's onClick path moves focus to the
      // button; this restores it.)
      const ta = this.textareaRef.current;
      if ( ta ) {
        ta.style.height = "auto";
        ta.focus();
      }
      this.scrollToBottom();
    });

    const api = this.getApi();
    // Only forward metadata if the user actually attached some — the
    // backend's schema drops the field when undefined.
    const parentId = undefined;
    api.threadItemPost(revisionId, sessionId, alias, content, parentId, metadata || undefined)
      .then( (response) => {
        if ( !this.mounted ) return;
        const serverItem = response.data && response.data["item"];
        if ( !serverItem ) {
          // No item in response — replace with failed state.
          this.setState({
            items: this.state.items.map(i =>
              i.__tempId === tempId
                ? Object.assign({}, i, { __pending: false, __failed: true })
                : i
            ),
            posting: false,
            postError: "Server did not return a thread item."
          });
          this.pendingTempIds.delete(tempId);
          return;
        }

        // Functional setState so we read fresh state — the optimistic row
        // can be missing if something refetched between sendItem and this
        // response (e.g. a sync resume that ran initialLoad). In that
        // case the server item won't have been picked up by the refetch
        // either (race window), so append it explicitly.
        this.setState((prev) => {
          const items = prev.items;
          const tempIdx = items.findIndex(i => i.__tempId === tempId);
          let nextItems;
          if ( tempIdx >= 0 ) {
            // Normal path: replace the optimistic row with the server item.
            nextItems = items.map(i => i.__tempId === tempId ? serverItem : i);
          } else if ( items.some(i => i.id === serverItem.id) ) {
            // Refetch already brought the server item in — leave as-is.
            nextItems = items;
          } else {
            // State was wiped/refetched and didn't pick up the server item.
            // Append it to recover.
            nextItems = [...items, serverItem];
          }
          const prevNewest = prev.newestCreatedAt;
          const nextNewest = (!prevNewest || serverItem.created_at > prevNewest)
            ? serverItem.created_at
            : prevNewest;
          return {
            items: nextItems,
            newestCreatedAt: nextNewest,
            posting: false,
            postError: null
          };
        });
        this.pendingTempIds.delete(tempId);
      }, (error) => {
        if ( !this.mounted ) return;
        this.setState({
          items: this.state.items.map(i =>
            i.__tempId === tempId
              ? Object.assign({}, i, { __pending: false, __failed: true })
              : i
          ),
          posting: false,
          postError: error && error.message ? error.message : "Failed to post thread item."
        });
        // Keep tempId in pendingTempIds so the user can retry.
      });
  }

  /**
   * Composer metadata attachment
   */

  openComposerMetadataModal = () => {
    const draft = this.state.composerMetadata
      ? JSON.stringify(this.state.composerMetadata, null, 2)
      : "{\n  \n}";
    this.setState({
      composerMetadataModalVisible: true,
      composerMetadataDraft: draft,
      composerMetadataError: null
    });
  }

  closeComposerMetadataModal = () => {
    this.setState({
      composerMetadataModalVisible: false,
      composerMetadataError: null
    });
  }

  applyComposerMetadata = () => {
    const text = (this.state.composerMetadataDraft || "").trim();
    if ( text === "" ) {
      // Empty metadata is treated as "no attachment".
      this.setState({
        composerMetadata: null,
        composerMetadataModalVisible: false,
        composerMetadataError: null
      });
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      this.setState({ composerMetadataError: "Invalid JSON: " + e.message });
      return;
    }
    if ( typeof parsed !== "object" || parsed === null || Array.isArray(parsed) ) {
      this.setState({ composerMetadataError: "Metadata must be a JSON object." });
      return;
    }
    this.setState({
      composerMetadata: parsed,
      composerMetadataModalVisible: false,
      composerMetadataError: null
    });
  }

  clearComposerMetadata = () => {
    this.setState({ composerMetadata: null });
  }

  /**
   * Item expansion (long content + metadata)
   */

  toggleInSet(stateKey, itemId) {
    if ( !itemId ) return;
    const next = new Set(this.state[stateKey]);
    if ( next.has(itemId) ) {
      next.delete(itemId);
    } else {
      next.add(itemId);
    }
    this.setState({ [stateKey]: next });
  }

  toggleContent(itemId) {
    this.toggleInSet("expandedContent", itemId);
  }

  toggleMetadata(itemId) {
    this.toggleInSet("expandedMetadata", itemId);
  }

  registerContentRef = (itemId, index, el) => {
    if ( !itemId ) return;
    if ( !this.contentRefs[itemId] ) this.contentRefs[itemId] = [];
    if ( el ) {
      this.contentRefs[itemId][index] = el;
    } else if ( this.contentRefs[itemId] ) {
      this.contentRefs[itemId][index] = null;
    }
  }

  // Compare rendered scroll-height vs offset-height for each tracked
  // content block and flip overflowingItems accordingly. Items in the
  // expanded state are skipped (no max-height applied, so they can't
  // be in overflow). Called from componentDidUpdate so the toggle
  // shows/hides as soon as layout settles.
  checkContentOverflow() {
    const next = new Set();
    const expanded = this.state.expandedContent;
    for ( const itemId of Object.keys(this.contentRefs) ) {
      if ( expanded.has(itemId) ) {
        // Carry forward — once flagged, an item stays "could overflow"
        // until we get a fresh measurement in its collapsed state. We
        // can't measure while expanded because the cap is lifted.
        if ( this.state.overflowingItems.has(itemId) ) next.add(itemId);
        continue;
      }
      const refs = this.contentRefs[itemId] || [];
      for ( const el of refs ) {
        if ( el && el.scrollHeight > el.offsetHeight + 1 ) {
          next.add(itemId);
          break;
        }
      }
    }
    const current = this.state.overflowingItems;
    let changed = next.size !== current.size;
    if ( !changed ) {
      for ( const id of next ) {
        if ( !current.has(id) ) { changed = true; break; }
      }
    }
    if ( changed ) {
      this.setState({ overflowingItems: next });
    }
  }

  retryFailedItem(item) {
    if ( !item || !item.__failed ) return;
    const text = (item.content && item.content[0] && item.content[0].text) || "";
    if ( !text ) return;
    // Remove failed row, then re-send the text.
    this.setState({
      items: this.state.items.filter(i => i.__tempId !== item.__tempId)
    }, () => {
      this.pendingTempIds.delete(item.__tempId);
      this.sendItem(text);
    });
  }

  /**
   * Scroll handling
   */

  handleScroll = (e) => {
    const el = e.target;
    this.wasAtBottom = (el.scrollTop + el.clientHeight) >= (el.scrollHeight - 10);
    if ( this.wasAtBottom && this.state.unseenCount > 0 ) {
      this.setState({ unseenCount: 0 });
    }
    if ( el.scrollTop < 40 && this.state.hasMoreOlder && !this.state.loadingOlder ) {
      this.loadOlder();
    }
  };

  scrollToBottom() {
    requestAnimationFrame(() => {
      const el = this.scrollContainerRef.current;
      if ( el ) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }

  /**
   * Composer handlers
   */

  handleKeyDown = (e) => {
    if ( e.key === "Enter" && !e.shiftKey ) {
      e.preventDefault();
      this.sendItem();
    }
  };

  handleTextareaInput = (e) => {
    const value = e.target.value;
    this.setState({ inputValue: value });
    const el = e.target;
    el.style.height = "auto";
    const lineHeight = 20;
    const maxHeight = lineHeight * 6;
    el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";
  };

  /**
   * Author name resolution (cached)
   */

  resolveAuthorName(userId) {
    if ( !userId ) return "unknown";
    const api = this.getApi();
    const cachedResponse = api.usersCacheById ? api.usersCacheById[userId] : null;
    if ( cachedResponse ) {
      const user = (cachedResponse.data && cachedResponse.data["user"]) || {};
      return user["name"] || user["alias"] || user["email"] || userId.slice(-5);
    }
    if ( !this.authorPromises[userId] ) {
      this.authorPromises[userId] = api.usersIdGet(userId).then(() => {
        if ( this.mounted ) this.forceUpdate();
      }, () => {
        delete this.authorPromises[userId];
      });
    }
    return userId.slice(-5);
  }

  /**
   * Rendering
   */

  getClassName() {
    return "playground-thread-node";
  }

  renderHeaderAlias() {
    const alias = this.getBoundAlias();
    if ( !alias ) return null;
    return <span className="playground-widget-alias">{alias}</span>;
  }

  renderHeaderActions() {
    const envelope = this.getEnvelopeState();
    const value = envelope.status === "ready" ? envelope.value : null;
    return (
      <EnvelopeControls
        alias={this.getBoundAlias()}
        value={value}
        shellInterface={this.props.data.shellInterface}
        sessionManager={this.context}
      />
    );
  }

  renderTextContent(text, contentView) {
    if ( typeof text !== "string" ) {
      return null;
    }
    if ( "raw" === contentView ) {
      return <pre className="playground-thread-text-raw">{text}</pre>;
    }
    if ( "json" === contentView ) {
      let pretty = text;
      try { pretty = JSON.stringify(JSON.parse(text), null, 2); } catch (e) { /* fall through */ }
      return <pre className="playground-thread-text-json">{pretty}</pre>;
    }
    // Markdown (default)
    return <MarkdownBody text={text} />;
  }

  // Concatenate all content items into a single string suitable for the
   // clipboard. Text items pass through as-is; json items are pretty-printed.
  extractItemTextForCopy(item) {
    const parts = [];
    for ( const c of (item.content || []) ) {
      if ( "text" === c.type && typeof c.text === "string" ) {
        parts.push(c.text);
      } else if ( "json" === c.type ) {
        try { parts.push(JSON.stringify(c.json, null, 2)); }
        catch (e) { /* skip — unserializable */ }
      }
    }
    return parts.join("\n\n");
  }

  renderItemActions(item) {
    // Don't surface actions for in-flight optimistic posts — there's no
    // server item to act on yet, and showing them would be misleading.
    if ( item.__pending ) return null;

    const hasMetadata = !!(item.metadata && typeof item.metadata === "object");
    const overflows = this.state.overflowingItems.has(item.id);
    const contentExpanded = this.state.expandedContent.has(item.id);
    const metadataExpanded = this.state.expandedMetadata.has(item.id);

    return (
      <span className="playground-thread-actions">
        <ThreadItemAction
          tooltip="Copy to clipboard"
          confirmTooltip="Copied"
          icon={<LuClipboardCopy />}
          confirmIcon={<LuCheck />}
          onActivate={() => Clipboard.to(this.extractItemTextForCopy(item))}
        />
        {overflows &&
          <ThreadItemAction
            tooltip={contentExpanded ? "Collapse" : "Expand"}
            icon={contentExpanded ? <LuChevronUp /> : <LuChevronDown />}
            onActivate={() => this.toggleContent(item.id)}
          />
        }
        {hasMetadata &&
          <ThreadItemAction
            tooltip={metadataExpanded ? "Hide metadata" : "Show metadata"}
            icon={<CgDetailsMore />}
            onActivate={() => this.toggleMetadata(item.id)}
          />
        }
      </span>
    );
  }

  renderItemContent(item, contentView, isExpanded) {
    return (item.content || []).map((contentItem, ci) => {
      let inner = null;
      if ( "text" === contentItem.type ) {
        inner = this.renderTextContent(contentItem.text, contentView);
      } else if ( "json" === contentItem.type ) {
        const text = JSON.stringify(contentItem.json, null, 2);
        inner = <pre className="playground-thread-text-json">{text}</pre>;
      }
      if ( inner === null ) return null;
      // Wrap each content item so a sibling-selector rule can draw the
      // dashed separator between them and so collapsed-state max-height
      // can be applied. The callback ref hooks the element into
      // checkContentOverflow() for measuring rendered vs intrinsic
      // height — that determines whether the expand toggle surfaces.
      const cls = "playground-thread-content-item"
        + (isExpanded ? "" : " is-collapsed");
      return (
        <div
          key={ci}
          className={cls}
          ref={(el) => this.registerContentRef(item.id, ci, el)}
        >
          {inner}
        </div>
      );
    });
  }

  renderItem(item, index, contentView) {
    const isSelf = item.user_id && this.state.currentUserId && item.user_id === this.state.currentUserId;
    const itemKey = item.id || item.__tempId || index;
    const timezone = this.props.preferences && this.props.preferences["app.timezone"];
    const timeAgo = item.created_at ? Utils.formatTimeAgo(item.created_at) : null;
    const timeAbs = item.created_at ? Utils.formatDateTime(item.created_at, timezone) : null;
    const contentExpanded = this.state.expandedContent.has(item.id);
    const metadataExpanded = this.state.expandedMetadata.has(item.id);
    const body = this.renderItemContent(item, contentView, contentExpanded);

    const failedFooter = item.__failed && (
      <div className="playground-thread-failed">
        <span>Failed to send.</span>
        <Button
          size="sm"
          className="playground-thread-failed-retry"
          onClick={() => this.retryFailedItem(item)}
        >
          Retry
        </Button>
      </div>
    );

    const sideClass = isSelf ? "playground-thread-row-self" : "playground-thread-row-other";
    let bubbleClass = isSelf ? "playground-thread-bubble-self" : "playground-thread-bubble-other";
    if ( item.__pending ) bubbleClass += " playground-thread-bubble-pending";
    if ( item.__failed ) bubbleClass += " playground-thread-bubble-failed";

    // Self messages don't get an author label — it's redundant ("self"
    // says nothing useful next to a bubble on the right). Other-side
    // bubbles show a clickable author name.
    let authorEl = null;
    if ( !isSelf ) {
      const authorName = this.resolveAuthorName(item.user_id);
      const profilePath = this.resolveProfilePath(item.user_id);
      authorEl = profilePath
        ? <a
            className="playground-thread-meta-author playground-thread-meta-author-link"
            href={profilePath}
            target="_blank"
            rel="noreferrer"
          >{authorName}</a>
        : <span className="playground-thread-meta-author">{authorName}</span>;
    }

    const hasMetadata = !!(item.metadata && typeof item.metadata === "object");

    return (
      <div key={itemKey} className={"playground-thread-row " + sideClass}>
        <div className={"playground-thread-bubble " + bubbleClass}>
          {body}
          {metadataExpanded && hasMetadata &&
            <div className="playground-thread-metadata-block">
              <div className="playground-thread-metadata-label">metadata</div>
              <pre className="playground-thread-metadata-json">
                {JSON.stringify(item.metadata, null, 2)}
              </pre>
            </div>
          }
          {failedFooter}
        </div>
        <div className="playground-thread-meta">
          {authorEl}
          {timeAgo &&
            <>
              {authorEl && <span className="playground-thread-meta-sep">&middot;</span>}
              <Tooltip text={timeAbs}>
                <span className="playground-thread-meta-time">{timeAgo}</span>
              </Tooltip>
            </>
          }
          {(() => {
            const actionsEl = this.renderItemActions(item);
            if ( !actionsEl ) return null;
            return (
              <>
                <span className="playground-thread-meta-sep">&middot;</span>
                {actionsEl}
              </>
            );
          })()}
        </div>
      </div>
    );
  }

  resolveProfilePath(userId) {
    if ( !userId ) return null;
    // Prefer the user's alias for a cleaner URL, falling back to the id.
    const api = this.getApi();
    const cached = api && api.usersCacheById ? api.usersCacheById[userId] : null;
    const user = cached && cached.data ? cached.data.user || cached.data : null;
    const slug = (user && user.alias) || userId;
    return Navigation.userProfilePath(slug);
  }

  renderContent() {
    const { width, height, maxHeight } = this.getEffectiveSize();

    const contentView = Converter.getComponentPortValueData(this.props.data.component, "content_view");
    const envelopeState = this.getEnvelopeState();
    const isReady = envelopeState.status === "ready";

    const canEdit = !!this.props.data.shellInterface.canEditRevision();
    const showComposer = canEdit && isReady;

    return (
      <div ref={this.nodeRef} className="playground-thread-node" style={{ width, height, maxHeight }}>
        {envelopeState.status === "unbound" &&
          this.renderNotice({ variant: "info", message: 'Connect a value to the "thread" port.' })
        }
        {envelopeState.status === "no-session" &&
          this.renderNotice({ variant: "info", message: "Select a session to view this widget." })
        }
        {envelopeState.status === "loading" &&
          this.renderLoading(envelopeState.reason || "Loading…")
        }
        {envelopeState.status === "missing" &&
          this.renderNotice({
            variant: "info",
            message: `"${envelopeState.alias}" is not available in the selected session yet.`,
          })
        }
        {envelopeState.status === "wrong-type" &&
          this.renderNotice({
            variant: "warning",
            message: `The "thread" port expects a "thread" value, but "${envelopeState.alias}" is "${envelopeState.type}".`,
          })
        }
        {isReady && this.state.loadError &&
          this.renderNotice({ variant: "warning", message: this.state.loadError })
        }

        {isReady && this.state.loading &&
          this.renderLoading("Loading thread…")
        }

        {isReady &&
          <div
            ref={this.scrollContainerRef}
            className="playground-thread-node-messages nodrag nowheel"
            onScroll={this.handleScroll}
          >
            {this.state.loadingOlder &&
              this.renderLoading("Loading earlier messages…")
            }
            {(this.state.items.length === 0 && !this.state.loading) &&
              <div className="playground-thread-node-empty">
                No messages yet. Type something below to start the thread.
              </div>
            }
            {this.state.items.map((item, index) => this.renderItem(item, index, contentView))}
          </div>
        }

        {this.state.unseenCount > 0 &&
          <div
            className="playground-thread-new-messages-pill nodrag"
            onClick={() => {
              this.scrollToBottom();
              this.setState({ unseenCount: 0 });
            }}
          >
            &darr; {this.state.unseenCount} new
          </div>
        }

        {showComposer &&
          <div className="playground-thread-composer">
            {this.state.postError &&
              <Alert variant="warning" className="playground-thread-composer-error">
                {this.state.postError}
              </Alert>
            }
            {this.state.composerMetadata &&
              <button
                type="button"
                className="playground-thread-composer-attached"
                onClick={this.openComposerMetadataModal}
              >
                <CgDetailsMore className="playground-thread-composer-attached-icon" />
                <span className="playground-thread-composer-attached-label">
                  metadata
                </span>
                <Tooltip text="Remove">
                  <span
                    role="button"
                    tabIndex={0}
                    className="playground-thread-composer-attached-remove"
                    onClick={(e) => { e.stopPropagation(); this.clearComposerMetadata(); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        this.clearComposerMetadata();
                      }
                    }}
                  >
                    <LuX />
                  </span>
                </Tooltip>
              </button>
            }
            <Form onSubmit={(event) => { event.preventDefault(); this.sendItem(); }}>
              <div className="playground-thread-composer-shell">
                <Dropdown drop="up">
                  <Dropdown.Toggle as={ComposerAttachToggle} />
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={this.openComposerMetadataModal}>
                      <CgDetailsMore />&nbsp;&nbsp;{this.state.composerMetadata ? "Edit Metadata" : "Add Metadata"}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                <Form.Control
                  as="textarea"
                  rows={1}
                  ref={this.textareaRef}
                  className="playground-thread-composer-textarea nodrag nowheel"
                  placeholder="Type a message..."
                  value={this.state.inputValue}
                  onChange={this.handleTextareaInput}
                  onKeyDown={this.handleKeyDown}
                />
                <Tooltip text="Send (Shift+Enter for newline)">
                  <Button
                    className="playground-thread-composer-send"
                    disabled={!this.canSend()}
                    onClick={() => this.sendItem()}
                  >
                    <VscSend />
                  </Button>
                </Tooltip>
              </div>
            </Form>
          </div>
        }

        {this.renderMetadataModal()}
      </div>
    );
  }

  renderMetadataModal() {
    if ( !this.state.composerMetadataModalVisible ) return null;
    return (
      <Modal
        show={this.state.composerMetadataModalVisible}
        onHide={this.closeComposerMetadataModal}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Attach Metadata</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AceEditor
            name="playground-thread-composer-metadata-editor"
            width="100%"
            mode="json"
            theme="monokai"
            wrapEnabled="true"
            value={this.state.composerMetadataDraft}
            onChange={(value) => this.setState({
              composerMetadataDraft: value, composerMetadataError: null
            })}
            minLines={5}
            maxLines={24}
            setOptions={{ printMarginColumn: 120 }}
          />
          <div style={{ marginTop: "10px", color: "#6b7280", fontSize: "13px" }}>
            Metadata attached to the next sent message. Must be a JSON object.
            Leave empty to send without metadata.
          </div>
          {this.state.composerMetadataError &&
            <Alert variant="warning" style={{ marginTop: "10px", marginBottom: 0 }}>
              {this.state.composerMetadataError}
            </Alert>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={this.closeComposerMetadataModal}>
            Cancel
          </Button>
          <Button variant="success" onClick={this.applyComposerMetadata}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default withPreferences(
  ThreadNode,
  ["app.timezone"],
  // Resolve preferences with the same org/blob scope the playground shell
  // uses, so blob- or org-level timezone overrides are honored. Without
  // this, withPreferences falls back to the user-level scope only and any
  // blob-scoped timezone is silently ignored.
  props => props.data.shellInterface.getScope()
);
